import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { ToggleGroup, ToggleGroupItem } from '@hqo/ui-atoms';
import { cn } from '@hqo/react-hooks';
import { ActivitySegment } from 'app/data/types/activity';

interface ActivitySegmentedProps {
  value: ActivitySegment;
  onChange: (segment: ActivitySegment) => void;
  /**
   * Per-segment record counts. A segment with a number gets a badge; one without gets none, which
   * is how "still loading" and "count we do not trust" render as simply nothing.
   *
   * Keyed by segment rather than taken as a single `upcomingCount` so putting a badge on Past later
   * is a change at the call site and not in here.
   */
  counts?: Partial<Record<ActivitySegment, number>>;
  className?: string;
}

const SEGMENTS: { key: ActivitySegment; labelId: string }[] = [
  { key: 'upcoming', labelId: 'activity.segment.upcoming' },
  { key: 'completed', labelId: 'activity.segment.completed' },
];

/**
 * Upcoming / Past control — the DS `ToggleGroup` (single-select), styled as underlined tabs: labels
 * left-aligned on a light grey hairline, the active one in foreground text with a dark rule beneath.
 *
 * Still a `ToggleGroup` rather than hand-rolled tabs. It is the wrong semantic name for the look but
 * the right behaviour — single-select, roving focus, arrow keys — and swapping it would trade all of
 * that for a visual change, plus break the `activity-segment-*` ids the specs and the cypress smoke
 * test select on.
 */
export const ActivitySegmented = ({ value, onChange, counts, className }: ActivitySegmentedProps): JSX.Element => {
  const intl = useIntl();
  // ToggleGroup single allows deselect (value → ''); a segmented control never deselects.
  const handleChange = useCallback((next: string) => {
    if (next) onChange(next as ActivitySegment);
  }, [onChange]);

  return (
    <ToggleGroup
      type="single"
      value={value}
      onValueChange={handleChange}
      aria-label={intl.formatMessage({ id: 'activity.segment.aria' })}
      data-testid="activity-segmented"
      // The hairline is the group's own bottom border, so it runs the full width regardless of how
      // many tabs there are or how wide their labels get. `rounded-none` because the DS group is
      // `rounded-[8px]`, which would curve the ends of that hairline.
      //
      // No `gap`: the tabs touch. The pills carry their own horizontal padding (on the inner span
      // below), so the active rule spans a whole pill and, with the pills flush, the newly-active
      // rule sits immediately beside where the previous one was — switching moves it with no gap to
      // jump across.
      className={cn(
        'flex w-full justify-start rounded-none border-b border-system-grey-light',
        className,
      )}
    >
      {SEGMENTS.map(({ key, labelId }) => (
        <ToggleGroupItem
          key={key}
          value={key}
          data-testid={`activity-segment-${key}`}
          // Override the DS toggle states to leave nothing but text and a rule. The HqO
          // ToggleGroupItem bakes in BOTH `hover:*` and a `data-[state=on]:hover:bg-system-blue`
          // (+ text-shared-white) on-state hover — each a distinct modifier stack, so all three
          // must be overridden explicitly (tailwind-merge only dedupes matching stacks).
          //
          // The three `!`s are not laziness, they are the only deterministic option. The DS item
          // carries `rounded-none first:rounded-l-[8px] last:rounded-r-[8px]`, `border-y border-r
          // first:border-l` and `p-[8px_16px]` — and tailwind-merge treats side/axis/arbitrary
          // utilities as DIFFERENT groups from the all-round ones, so a plain `border-0` does not
          // displace `border-y`/`first:border-l`; both survive and stylesheet order decides. That is
          // what put a left border and a rounded bottom-left corner on the active tab. Importance
          // wins regardless of order.
          //
          // `w-auto flex-none` because the DS item is `flex-1`: stretched, the rule below would run
          // under the full half-width instead of under the word.
          className="group/tab relative w-auto flex-none rounded-none! border-0! bg-transparent p-0! text-[15px] font-semibold text-system-grey-dark-alt shadow-none hover:bg-transparent hover:text-system-grey-dark data-[state=on]:bg-transparent data-[state=on]:text-primary data-[state=on]:shadow-none data-[state=on]:hover:bg-transparent data-[state=on]:hover:text-primary"
        >
          {/* Owns ALL the pill spacing — horizontal `px-3` and the `pb-2.5` the rule needs — so it
              rides on this element rather than the DS item (killing the item's padding above cannot
              collapse either). Because the item is `w-auto`, it takes this span's width, so the rule
              (`left-0 right-0` on the item) spans the whole padded pill, not just the text; with the
              group gap gone, adjacent pills touch and their rules are contiguous. `flex` so the badge
              sits on the label's baseline row and the rule spans both — label AND count. */}
          <span className="flex items-center gap-2 px-3 pb-2.5">
            {intl.formatMessage({ id: labelId })}
            {counts?.[key] !== undefined && (
              <span
                data-testid={`activity-segment-count-${key}`}
                // No text colour of its own on purpose: the count INHERITS the tab's, so it follows
                // the same grey → hover → tenant-primary states as the label beside it and cannot
                // drift from them. Its bg is the one fixed part: a neutral off-token grey while the
                // tab is inactive (rgb(240,242,244), from the design mock and not in the DS), and on
                // the active tab the tenant's own primary at the same 10% the host paints its
                // content-tile category pills with — so the bubble picks up the building's colour
                // instead of the fixed navy tint it carried before.
                className="rounded-full bg-[rgb(240,242,244)] px-2 py-1 text-[13px] font-semibold leading-none group-data-[state=on]/tab:bg-primary/10"
              >
                {intl.formatNumber(counts[key] as number)}
              </span>
            )}
          </span>
          {/* The rule, drawn rather than bordered.
            *
            * A child element instead of the item's `border-b`: a border has to out-specify the DS
            * border utilities (see above) and would inherit the item's radius at the corners, and
            * `border-primary` is not even generated in this app — only `bg-primary` and
            * `text-primary` are. `-bottom-px` lands it ON the group's hairline rather than a pixel
            * above. Always mounted and toggled by opacity, so switching tabs moves a rule instead of
            * reflowing the row. */}
          <span
            aria-hidden
            className="pointer-events-none absolute -bottom-px left-0 right-0 h-0.5 bg-primary opacity-0 group-data-[state=on]/tab:opacity-100"
          />
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
};
